import React, { useEffect, useMemo, useState } from "react";
import api from "../api/axios";

import SessionsToolbar from "../components/SessionsToolbar";
import SessionCard from "../components/SessionCard";
import SessionPage from "./SessionPage";
import "./SessionsPage.responsive.css";

function normalizeSession(session, patientName = "Patient") {
  return {
    id: session.id,
    patient_id: session.patient_id,
    title: session.title || "Clinical Session",
    created_at: session.created_at || null,
    started_at: session.started_at || null,
    owner: patientName,
    severity: session.severity || null,
    status: session.status || "draft",
    summary: session.summary || "No summary available yet.",
    tags: session.tags || [],
    raw: session,
  };
}

function StatCard({ label, value, helper, primary = false }) {
  return (
    <div className={primary ? "mgsessions-stat primary" : "mgsessions-stat"}>
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{helper}</p>
    </div>
  );
}

function SessionsSignalGraph({ stats }) {
  const maxValue = Math.max(stats.total, stats.reviewed, stats.highPriority, 1);

  const bars = [
    {
      label: "Total",
      value: stats.total,
      color: "linear-gradient(180deg, #18b7ff, #1f7bc1)",
    },
    {
      label: "Reviewed",
      value: stats.reviewed,
      color: "linear-gradient(180deg, #62d7ff, #2d8fe0)",
    },
    {
      label: "Priority",
      value: stats.highPriority,
      color: "linear-gradient(180deg, #fbbf24, #f59e0b)",
    },
  ];

  return (
    <div className="mgsessions-signal-card">
      <div className="mgsessions-signal-head">
        <div>
          <span>Session Signal</span>
          <strong>Clinical activity</strong>
        </div>
        <em>Live</em>
      </div>

      <div className="mgsessions-bars">
        {bars.map((bar) => {
          const height = Math.max(20, Math.round((bar.value / maxValue) * 100));

          return (
            <div className="mgsessions-bar-item" key={bar.label}>
              <div className="mgsessions-bar-track">
                <i
                  style={{
                    height: `${height}%`,
                    background: bar.color,
                  }}
                />
              </div>
              <b>{bar.value}</b>
              <small>{bar.label}</small>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WorkflowIntelligence({ stats }) {
  const pending = Math.max(stats.total - stats.reviewed, 0);

  const workflow = [
    {
      title: "Patient Intake",
      helper: "Structured symptoms",
      active: stats.total > 0,
    },
    {
      title: "MedGen Analysis",
      helper: "Clinical reasoning",
      active: stats.total > 0,
    },
    {
      title: "Doctor Review",
      helper: "Reviewer workflow",
      active: stats.reviewed > 0,
    },
    {
      title: "Brain Map",
      helper: "Impact regions",
      active: stats.reviewed > 0,
    },
    {
      title: "Case Sheet",
      helper: "PDF output",
      active: stats.reviewed > 0,
    },
  ];

  const trendBars = [
    {
      label: "Draft",
      value: pending,
      height: stats.total ? Math.max(28, (pending / stats.total) * 100) : 22,
      color: "linear-gradient(180deg, #94d8ff, #1f7bc1)",
    },
    {
      label: "Review",
      value: stats.reviewed,
      height: stats.total
        ? Math.max(28, (stats.reviewed / stats.total) * 100)
        : 22,
      color: "linear-gradient(180deg, #62d7ff, #2d8fe0)",
    },
    {
      label: "Priority",
      value: stats.highPriority,
      height: stats.total
        ? Math.max(20, (stats.highPriority / stats.total) * 100)
        : 18,
      color: "linear-gradient(180deg, #fbbf24, #f59e0b)",
    },
    {
      label: "Output",
      value: stats.reviewed,
      height: stats.total
        ? Math.max(28, (stats.reviewed / stats.total) * 100)
        : 22,
      color: "linear-gradient(180deg, #18b7ff, #1769a9)",
    },
  ];

  let nextAction = "Create the first clinical session";

  if (stats.total > 0 && pending > 0) {
    nextAction = "Continue pending session review";
  } else if (stats.total > 0 && pending === 0) {
    nextAction = "Open reviewed sessions or create new intake";
  }

  if (stats.highPriority > 0) {
    nextAction = "Review high-priority sessions first";
  }

  return (
    <div className="mgsessions-intelligence-card">
      <div className="mgsessions-intel-head">
        <div>
          <span>Clinical Workflow Intelligence</span>
          <strong>Session pipeline</strong>
        </div>
        <em>{pending} pending</em>
      </div>

      <div className="mgsessions-workflow-line">
        {workflow.map((item, index) => (
          <div
            key={item.title}
            className={
              item.active
                ? "mgsessions-workflow-step active"
                : "mgsessions-workflow-step"
            }
          >
            <div className="mgsessions-workflow-node">
              <b>{index + 1}</b>
            </div>
            <div>
              <strong>{item.title}</strong>
              <small>{item.helper}</small>
            </div>
          </div>
        ))}
      </div>

      <div className="mgsessions-intel-bottom">
        <div className="mgsessions-mini-chart">
          {trendBars.map((bar) => (
            <div className="mgsessions-mini-item" key={bar.label}>
              <div className="mgsessions-mini-track">
                <i
                  style={{
                    height: `${bar.height}%`,
                    background: bar.color,
                  }}
                />
              </div>
              <b>{bar.value}</b>
              <small>{bar.label}</small>
            </div>
          ))}
        </div>

        <div className="mgsessions-next-action">
          <span>Next Action</span>
          <strong>{nextAction}</strong>
          <p>
            Keep sessions moving from intake to analysis, review, brain mapping,
            and final clinical sheet.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function SessionsPage({ user }) {
  const [patients, setPatients] = useState([]);
  const [sessions, setSessions] = useState([]);

  const [loadingPatients, setLoadingPatients] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(true);

  const [selectedSessionId, setSelectedSessionId] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [patientFilter, setPatientFilter] = useState("all");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    loadPatients();
  }, []);

  useEffect(() => {
    if (patients.length) {
      loadAllSessions(patients);
    } else if (!loadingPatients) {
      setSessions([]);
      setLoadingSessions(false);
    }
    
  }, [patients, loadingPatients]);

  const loadPatients = async () => {
    try {
      setLoadingPatients(true);
      const res = await api.get("/api/patient/");
      setPatients(res.data || []);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not load patients");
    } finally {
      setLoadingPatients(false);
    }
  };

  const loadAllSessions = async (patientList) => {
    try {
      setLoadingSessions(true);

      const results = await Promise.all(
        patientList.map(async (patient) => {
          try {
            const res = await api.get(`/api/session/patient/${patient.id}`);
            return (res.data || []).map((session) =>
              normalizeSession(session, patient.name || "Patient")
            );
          } catch {
            return [];
          }
        })
      );

      setSessions(results.flat());
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not load sessions");
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleNewSession = async () => {
    if (!patients.length) {
      alert("Create a patient first before starting a session.");
      return;
    }

    const targetPatient =
      patientFilter !== "all"
        ? patients.find((p) => String(p.id) === String(patientFilter))
        : patients[0];

    if (!targetPatient) {
      alert("Please select a patient first.");
      return;
    }

    try {
      const res = await api.post("/api/session/", {
        patient_id: targetPatient.id,
        title: "Clinical Session",
        modality: "text",
      });

      await loadAllSessions(patients);
      setSelectedSessionId(res.data.id);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not create session");
    }
  };

  const filteredSessions = useMemo(() => {
    let next = [...sessions];

    next = next.filter((session) => {
      const q = search.trim().toLowerCase();
      const haystack = [
        session.title,
        session.summary,
        session.status,
        session.severity,
        session.owner,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch = !q || haystack.includes(q);

      const matchesStatus =
        statusFilter === "all" ||
        (session.status || "").toLowerCase() === statusFilter.toLowerCase();

      const matchesSeverity =
        severityFilter === "all" ||
        (session.severity || "untriaged").toLowerCase() ===
          severityFilter.toLowerCase();

      const matchesPatient =
        patientFilter === "all" ||
        String(session.patient_id) === String(patientFilter);

      return matchesSearch && matchesStatus && matchesSeverity && matchesPatient;
    });

    next.sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
    return next;
  }, [sessions, search, statusFilter, severityFilter, patientFilter]);

  const visibleSessions = useMemo(() => {
    return showAll ? filteredSessions : filteredSessions.slice(0, 8);
  }, [filteredSessions, showAll]);

  const stats = useMemo(() => {
    return {
      total: sessions.length,
      reviewed: sessions.filter((s) =>
        ["reviewed", "completed"].includes((s.status || "").toLowerCase())
      ).length,
      highPriority: sessions.filter((s) =>
        ["critical", "high"].includes((s.severity || "").toLowerCase())
      ).length,
    };
  }, [sessions]);

  if (selectedSessionId) {
    return (
      <div className="mgsessions-page">
        <style>{sessionsCss}</style>
        <SessionPage
          sessionId={selectedSessionId}
          onBack={() => setSelectedSessionId(null)}
          user={user}
        />
      </div>
    );
  }

  return (
    <div className="mgsessions-page">
      <style>{sessionsCss}</style>

      <section className="mgsessions-hero">
        <div className="mgsessions-hero-copy">
          <div className="mgsessions-kicker">Session Workspace</div>
          <h1>Clinical Sessions</h1>
          <p>
            Search, filter, and continue patient sessions from a single MedGen
            clinical workspace.
          </p>

          <div className="mgsessions-hero-actions">
            <button
              className="mgsessions-btn primary"
              type="button"
              onClick={handleNewSession}
              disabled={!patients.length}
            >
              + New Session
            </button>

            <button
              className="mgsessions-btn soft"
              type="button"
              onClick={() => setShowAll((prev) => !prev)}
              disabled={loadingPatients || loadingSessions}
            >
              {showAll ? "Show less" : "See all sessions"}
            </button>
          </div>

          <WorkflowIntelligence stats={stats} />
        </div>

        <div className="mgsessions-hero-panel">
          <div className="mgsessions-panel-top">
            <div>
              <span>AI Status</span>
              <strong>Clinical session mode</strong>
            </div>
            <em>Ready</em>
          </div>

          <p>
            Session records are loaded with patient-linked context, status
            filters, severity signals, and reviewer workflow visibility.
          </p>

          <SessionsSignalGraph stats={stats} />
        </div>
      </section>

      <section className="mgsessions-stats-grid">
        <StatCard
          primary
          label="Total sessions"
          value={stats.total}
          helper="All sessions loaded into the timeline"
        />

        <StatCard
          label="Reviewed"
          value={stats.reviewed}
          helper="Sessions completed by reviewer workflow"
        />

        <StatCard
          label="High priority"
          value={stats.highPriority}
          helper="Sessions marked high or critical"
        />
      </section>

      <SessionsToolbar
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        severityFilter={severityFilter}
        onSeverityFilterChange={setSeverityFilter}
        patientFilter={patientFilter}
        onPatientFilterChange={setPatientFilter}
        patients={patients}
        showAll={showAll}
        onToggleShowAll={() => setShowAll((prev) => !prev)}
        onNewSession={handleNewSession}
        disableActions={loadingPatients || loadingSessions || !patients.length}
      />

      {loadingPatients || loadingSessions ? (
        <div className="mgsessions-empty">Loading sessions...</div>
      ) : filteredSessions.length === 0 ? (
        <div className="mgsessions-empty">
          <h3>No sessions found</h3>
          <p>Create a session or adjust your filters.</p>
          <button
            className="mgsessions-btn primary"
            type="button"
            onClick={handleNewSession}
            disabled={!patients.length}
          >
            Start Session
          </button>
        </div>
      ) : (
        <div className="mgsessions-grid">
          {visibleSessions.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              onOpen={(s) => setSelectedSessionId(s.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const sessionsCss = `
.mgsessions-page {
  --mg-black: #03060c;
  --mg-navy: #06101f;
  --mg-navy-2: #081525;
  --mg-blue: #1f7bc1;
  --mg-cyan: #18b7ff;
  --mg-text: #07111f;
  --mg-muted: #66778e;
  --mg-border: #cddcec;
  --mg-soft: #f6faff;

  width: 100%;
  min-height: calc(100vh - 68px);
  padding: 28px 34px 46px;
  color: var(--mg-text);
}

.mgsessions-page * {
  box-sizing: border-box;
}

.mgsessions-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 430px;
  gap: 26px;
  align-items: stretch;
  margin-bottom: 22px;
  padding: 30px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid var(--mg-border);
  box-shadow: 0 20px 55px rgba(7, 17, 31, 0.07);
}

.mgsessions-kicker {
  margin-bottom: 12px;
  color: #125a94;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.mgsessions-hero h1 {
  margin: 0;
  color: var(--mg-text);
  font-size: clamp(38px, 4vw, 58px);
  line-height: 1.02;
  letter-spacing: -0.045em;
  font-weight: 950;
}

.mgsessions-hero p {
  max-width: 760px;
  margin: 18px 0 18px;
  color: var(--mg-muted);
  font-size: 15px;
  line-height: 1.7;
}

.mgsessions-hero-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 22px;
}

.mgsessions-btn {
  min-height: 40px;
  padding: 10px 16px;
  border-radius: 11px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.16s ease;
  white-space: nowrap;
}

.mgsessions-btn:disabled {
  opacity: 0.52;
  cursor: not-allowed;
}

.mgsessions-btn.primary {
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  box-shadow: 0 14px 28px rgba(31, 123, 193, 0.22);
}

.mgsessions-btn.soft,
.mgsessions-btn.ghost {
  color: #112238;
  background: #f8fbff;
  border-color: var(--mg-border);
}

.mgsessions-hero-panel {
  padding: 22px;
  border-radius: 22px;
  color: white;
  background:
    radial-gradient(circle at 92% 0%, rgba(24, 183, 255, 0.20), transparent 34%),
    linear-gradient(145deg, #03060c, #081525 62%, #06101f);
  border: 1px solid rgba(24,183,255,0.18);
  box-shadow: 0 20px 55px rgba(3, 6, 12, 0.16);
}

.mgsessions-panel-top {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.mgsessions-panel-top span {
  display: block;
  margin-bottom: 8px;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.mgsessions-panel-top strong {
  color: white;
  font-size: 22px;
  letter-spacing: -0.04em;
}

.mgsessions-panel-top em {
  padding: 5px 10px;
  border-radius: 999px;
  font-style: normal;
  color: #bfeaff;
  background: rgba(24,183,255,0.12);
  border: 1px solid rgba(24,183,255,0.18);
  font-size: 11px;
  font-weight: 900;
}

.mgsessions-hero-panel p {
  color: #a9bdd3;
  margin: 18px 0;
}

.mgsessions-signal-card {
  padding: 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.075);
  border: 1px solid rgba(24,183,255,0.13);
}

.mgsessions-signal-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.mgsessions-signal-head span {
  display: block;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.mgsessions-signal-head strong {
  color: white;
  font-size: 16px;
}

.mgsessions-signal-head em {
  color: #bfeaff;
  font-style: normal;
  font-size: 11px;
  font-weight: 900;
}

.mgsessions-bars {
  height: 145px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  align-items: end;
  margin-top: 16px;
}

.mgsessions-bar-item {
  display: grid;
  justify-items: center;
  gap: 7px;
}

.mgsessions-bar-track {
  width: 58px;
  height: 88px;
  display: flex;
  align-items: end;
  padding: 5px;
  border-radius: 16px;
  background: rgba(255,255,255,0.10);
  border: 1px solid rgba(255,255,255,0.10);
}

.mgsessions-bar-track i {
  width: 100%;
  min-height: 18px;
  border-radius: 12px;
  animation: mgsessionsBarRise 0.7s ease both;
}

.mgsessions-bar-item b {
  color: white;
  font-size: 18px;
  font-weight: 950;
}

.mgsessions-bar-item small {
  color: #a9bdd3;
  font-size: 11px;
  font-weight: 850;
}

.mgsessions-intelligence-card {
  margin-top: 10px;
  padding: 20px;
  border-radius: 22px;
  background:
    radial-gradient(circle at 96% 0%, rgba(24, 183, 255, 0.13), transparent 34%),
    linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
  border: 1px solid var(--mg-border);
  box-shadow: 0 14px 38px rgba(7, 17, 31, 0.055);
}

.mgsessions-intel-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 18px;
}

.mgsessions-intel-head span {
  display: block;
  margin-bottom: 7px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.17em;
  text-transform: uppercase;
}

.mgsessions-intel-head strong {
  display: block;
  color: var(--mg-text);
  font-size: 22px;
  font-weight: 950;
  letter-spacing: -0.045em;
}

.mgsessions-intel-head em {
  padding: 6px 11px;
  border-radius: 999px;
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border: 1px solid rgba(31, 123, 193, 0.18);
  font-size: 11px;
  font-weight: 900;
  font-style: normal;
  white-space: nowrap;
}

.mgsessions-workflow-line {
  position: relative;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.mgsessions-workflow-line::before {
  content: "";
  position: absolute;
  left: 8%;
  right: 8%;
  top: 23px;
  height: 3px;
  border-radius: 999px;
  background:
    linear-gradient(
      90deg,
      rgba(31,123,193,0.12),
      rgba(24,183,255,0.42),
      rgba(31,123,193,0.12)
    );
  background-size: 220% 100%;
  z-index: 0;
  animation: mgsessionsPipelineFlow 2.8s linear infinite;
  box-shadow: 0 0 18px rgba(24, 183, 255, 0.18);
}

.mgsessions-workflow-line::after {
  content: "";
  position: absolute;
  top: 19px;
  left: 8%;
  width: 11px;
  height: 11px;
  border-radius: 999px;
  z-index: 2;
  background: #18b7ff;
  box-shadow:
    0 0 0 6px rgba(24, 183, 255, 0.12),
    0 0 22px rgba(24, 183, 255, 0.55);
  animation: mgsessionsPipelineDot 4s ease-in-out infinite;
}

.mgsessions-workflow-step {
  position: relative;
  z-index: 1;
  display: grid;
  justify-items: center;
  text-align: center;
  gap: 8px;
  min-width: 0;
  animation: mgsessionsStepFloat 0.7s ease both;
}

.mgsessions-workflow-step:nth-child(1) {
  animation-delay: 0.04s;
}

.mgsessions-workflow-step:nth-child(2) {
  animation-delay: 0.12s;
}

.mgsessions-workflow-step:nth-child(3) {
  animation-delay: 0.20s;
}

.mgsessions-workflow-step:nth-child(4) {
  animation-delay: 0.28s;
}

.mgsessions-workflow-step:nth-child(5) {
  animation-delay: 0.36s;
}

.mgsessions-workflow-node {
  position: relative;
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: #eef6ff;
  border: 1px solid var(--mg-border);
  box-shadow: 0 8px 18px rgba(7, 17, 31, 0.05);
}

.mgsessions-workflow-node b {
  color: #0b4e84;
  font-size: 13px;
  font-weight: 950;
}

.mgsessions-workflow-step.active .mgsessions-workflow-node {
  background:
    radial-gradient(circle at 30% 20%, rgba(255,255,255,0.28), transparent 28%),
    linear-gradient(135deg, #1f7bc1, #0f5f9d);
  border-color: transparent;
  box-shadow:
    0 14px 28px rgba(31, 123, 193, 0.22),
    0 0 0 0 rgba(24, 183, 255, 0.22);
  animation: mgsessionsNodeBreath 2.2s ease-in-out infinite;
}

.mgsessions-workflow-step.active .mgsessions-workflow-node b {
  color: white;
}

.mgsessions-workflow-step.active .mgsessions-workflow-node::after {
  content: "";
  position: absolute;
  width: 9px;
  height: 9px;
  right: -2px;
  top: -2px;
  border-radius: 50%;
  background: #18b7ff;
  box-shadow:
    0 0 0 6px rgba(24, 183, 255, 0.14),
    0 0 20px rgba(24, 183, 255, 0.52);
  animation: mgsessionsPulse 1.8s infinite;
}

.mgsessions-workflow-step strong {
  color: var(--mg-text);
  font-size: 12px;
  font-weight: 950;
  line-height: 1.2;
}

.mgsessions-workflow-step small {
  display: block;
  margin-top: 3px;
  color: var(--mg-muted);
  font-size: 10px;
  font-weight: 750;
  line-height: 1.25;
}

.mgsessions-intel-bottom {
  display: grid;
  grid-template-columns: 310px minmax(0, 1fr);
  gap: 16px;
  align-items: stretch;
}

.mgsessions-mini-chart {
  min-height: 150px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  align-items: end;
  gap: 12px;
  padding: 16px;
  border-radius: 18px;
  background: #eef6ff;
  border: 1px solid #d9e8f6;
}

.mgsessions-mini-item {
  display: grid;
  justify-items: center;
  gap: 6px;
}

.mgsessions-mini-track {
  width: 46px;
  height: 82px;
  display: flex;
  align-items: end;
  padding: 5px;
  border-radius: 15px;
  background: #dfeaf6;
  border: 1px solid #d1e2f3;
}

.mgsessions-mini-track i {
  display: block;
  width: 100%;
  min-height: 18px;
  border-radius: 11px;
  animation: mgsessionsBarRise 0.7s ease both;
}

.mgsessions-mini-item b {
  color: var(--mg-text);
  font-size: 17px;
  font-weight: 950;
}

.mgsessions-mini-item small {
  color: var(--mg-muted);
  font-size: 10px;
  font-weight: 850;
}

.mgsessions-next-action {
  padding: 18px;
  border-radius: 18px;
  background:
    radial-gradient(circle at 100% 0%, rgba(31, 123, 193, 0.10), transparent 34%),
    #ffffff;
  border: 1px solid #d9e8f6;
}

.mgsessions-next-action span {
  display: block;
  margin-bottom: 8px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.mgsessions-next-action strong {
  display: block;
  color: var(--mg-text);
  font-size: 18px;
  font-weight: 950;
  letter-spacing: -0.035em;
}

.mgsessions-next-action p {
  margin: 10px 0 0;
  color: var(--mg-muted);
  font-size: 13px;
  line-height: 1.55;
}

.mgsessions-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}

.mgsessions-stat {
  min-height: 128px;
  padding: 20px;
  border-radius: 18px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.94);
  box-shadow: 0 12px 35px rgba(7, 17, 31, 0.055);
}

.mgsessions-stat.primary {
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #125a94);
  border-color: transparent;
}

.mgsessions-stat span {
  display: block;
  margin-bottom: 10px;
  color: #75869d;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgsessions-stat.primary span {
  color: rgba(255,255,255,0.75);
}

.mgsessions-stat strong {
  display: block;
  color: var(--mg-text);
  font-size: 28px;
  font-weight: 950;
}

.mgsessions-stat.primary strong {
  color: white;
}

.mgsessions-stat p {
  margin: 8px 0 0;
  color: var(--mg-muted);
  line-height: 1.5;
}

.mgsessions-stat.primary p {
  color: rgba(255,255,255,0.86);
}

.mgsessions-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  margin-bottom: 18px;
}

.mgsessions-toolbar-filters {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) 160px 160px 180px;
  gap: 10px;
}

.mgsessions-toolbar-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.mgsessions-filter-input,
.mgsessions-filter-select {
  width: 100%;
  min-height: 42px;
  padding: 10px 13px;
  border-radius: 12px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.96);
  color: #07111f;
  font-size: 13px;
  font-weight: 750;
  outline: none;
}

.mgsessions-filter-input:focus,
.mgsessions-filter-select:focus {
  border-color: #1f7bc1;
  box-shadow: 0 0 0 4px rgba(31, 123, 193, 0.12);
}

.mgsessions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  gap: 18px;
}

.mgsessions-empty {
  padding: 24px;
  border-radius: 18px;
  background: rgba(255,255,255,0.95);
  border: 1px solid var(--mg-border);
  box-shadow: 0 12px 35px rgba(7, 17, 31, 0.055);
}

.mgsessions-empty h3 {
  margin: 0 0 8px;
  color: var(--mg-text);
}

.mgsessions-empty p {
  color: var(--mg-muted);
}

.mgsession-card-button {
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.mgsession-card {
  min-height: 210px;
  padding: 20px;
  border-radius: 20px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.96);
  box-shadow: 0 14px 38px rgba(7,17,31,0.06);
  transition: 0.16s ease;
}

.mgsession-card:hover {
  transform: translateY(-2px);
  border-color: rgba(31, 123, 193, 0.55);
  box-shadow:
    0 0 0 1px rgba(24, 183, 255, 0.14),
    0 18px 44px rgba(31, 123, 193, 0.12);
}

.mgsession-card-top {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.mgsession-kicker {
  display: block;
  margin-bottom: 8px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.mgsession-card h3 {
  margin: 0 0 6px;
  color: #07111f;
  font-size: 20px;
  letter-spacing: -0.04em;
  font-weight: 950;
}

.mgsession-card p {
  margin: 0;
  color: #66778e;
  line-height: 1.55;
}

.mgsession-badge-stack {
  display: grid;
  gap: 7px;
  justify-items: end;
}

.mgsession-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 25px;
  padding: 5px 10px;
  border-radius: 999px;
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border: 1px solid rgba(31, 123, 193, 0.18);
  font-size: 11px;
  font-weight: 900;
  text-transform: capitalize;
}

.mgsession-pill.danger {
  color: #991b1b;
  background: rgba(239, 68, 68, 0.10);
  border-color: rgba(239, 68, 68, 0.22);
}

.mgsession-pill.warning {
  color: #92400e;
  background: rgba(245, 158, 11, 0.12);
  border-color: rgba(245, 158, 11, 0.24);
}

.mgsession-pill.soft,
.mgsession-pill.neutral {
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border-color: rgba(31, 123, 193, 0.18);
}

.mgsession-summary {
  margin-top: 18px !important;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mgsession-tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

@keyframes mgsessionsBarRise {
  from {
    transform: scaleY(0.4);
    opacity: 0.45;
    transform-origin: bottom;
  }

  to {
    transform: scaleY(1);
    opacity: 1;
    transform-origin: bottom;
  }
}

@keyframes mgsessionsPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(24, 183, 255, 0.22);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(24, 183, 255, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(24, 183, 255, 0);
  }
}

@keyframes mgsessionsPipelineFlow {
  0% {
    background-position: 220% 0;
  }

  100% {
    background-position: -220% 0;
  }
}

@keyframes mgsessionsPipelineDot {
  0% {
    left: 8%;
    opacity: 0;
    transform: scale(0.82);
  }

  8% {
    opacity: 1;
    transform: scale(1);
  }

  25% {
    left: 29%;
  }

  50% {
    left: 50%;
  }

  75% {
    left: 71%;
  }

  92% {
    opacity: 1;
  }

  100% {
    left: 88%;
    opacity: 0;
    transform: scale(0.82);
  }
}

@keyframes mgsessionsStepFloat {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes mgsessionsNodeBreath {
  0% {
    box-shadow:
      0 14px 28px rgba(31, 123, 193, 0.22),
      0 0 0 0 rgba(24, 183, 255, 0.18);
  }

  50% {
    box-shadow:
      0 18px 36px rgba(31, 123, 193, 0.28),
      0 0 0 7px rgba(24, 183, 255, 0.06);
  }

  100% {
    box-shadow:
      0 14px 28px rgba(31, 123, 193, 0.22),
      0 0 0 0 rgba(24, 183, 255, 0.18);
  }
}

@media (max-width: 1280px) {
  .mgsessions-hero {
    grid-template-columns: 1fr;
  }

  .mgsessions-hero-panel {
    max-width: 620px;
  }
}

@media (max-width: 1100px) {
  .mgsessions-page {
    padding: 20px;
  }

  .mgsessions-toolbar {
    grid-template-columns: 1fr;
  }

  .mgsessions-toolbar-filters {
    grid-template-columns: 1fr 1fr;
  }

  .mgsessions-toolbar-actions {
    justify-content: flex-start;
  }

  .mgsessions-intel-bottom {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 860px) {
  .mgsessions-workflow-line {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .mgsessions-workflow-line::before,
  .mgsessions-workflow-line::after {
    display: none;
  }

  .mgsessions-workflow-step {
    grid-template-columns: 46px 1fr;
    justify-items: start;
    text-align: left;
    padding: 12px;
    border-radius: 16px;
    background: rgba(255,255,255,0.75);
    border: 1px solid #d9e8f6;
  }
}

@media (max-width: 760px) {
  .mgsessions-page {
    padding: 14px;
  }

  .mgsessions-hero {
    padding: 20px;
  }

  .mgsessions-hero h1 {
    font-size: 36px;
  }

  .mgsessions-stats-grid {
    grid-template-columns: 1fr;
  }

  .mgsessions-grid {
    grid-template-columns: 1fr;
  }

  .mgsessions-toolbar-filters {
    grid-template-columns: 1fr;
  }

  .mgsessions-toolbar-actions {
    flex-direction: column;
  }

  .mgsession-card-top {
    flex-direction: column;
  }

  .mgsession-badge-stack {
    display: flex;
    flex-wrap: wrap;
    justify-items: start;
  }

  .mgsessions-mini-chart {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
`;